import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from '@itsrever/design-system'
import ExchangesApi from '@/infrastructure/api/exchanges'
import { rejection } from '@/application/manage-group-settings'
import { captureExchangesEvent, ExchangesEvent } from '@/infrastructure/posthog'
import {
  ExchangeOrders,
  ExchangePolicy,
  ExchangesKind,
  ExchangesSettings,
  FullCatalog,
  InstantExchange,
  priceCover
} from '@/domain/exchanges'

const exchangesKey = (group?: string) => ['groupExchanges', group]

export function useGroupExchanges(group?: string) {
  const query = useQuery({
    queryKey: exchangesKey(group),
    queryFn: ({ signal }) =>
      ExchangesApi.findGroupExchanges(group ?? '', signal),
    enabled: !!group
  })
  return {
    exchanges: query.data,
    loadingExchanges: query.isLoading,
    exchangesError: query.isError,
    reloadExchanges: query.refetch
  }
}

// The choices only: amounts and fees are left out.
function settingsSummary(kind: ExchangesKind, value: ExchangesSettings) {
  switch (kind) {
    case 'policy': {
      const policy = value as ExchangePolicy
      return {
        exchangeFlow: policy.exchangeFlow,
        priceCover: priceCover(policy.threshold),
        destinationScopedStock: policy.destinationScopedStock
      }
    }
    case 'orders': {
      const orders = value as ExchangeOrders
      return {
        price: orders.price,
        shippingLine: orders.shippingLine,
        paymentMethod: orders.paymentMethod || 'EXCHANGE_BY_REVER',
        carryOverDiscounts: orders.carryOverDiscounts
      }
    }
    case 'catalog': {
      const catalog = value as FullCatalog
      return {
        hasMenu: !!catalog.menuId,
        excludedProducts: catalog.excludedProductIds.length,
        excludedCollections: catalog.excludedCollectionIds.length,
        excludedTags: catalog.excludedTags.length,
        onlineStoreOnly: catalog.onlineStoreOnly
      }
    }
    case 'instant': {
      const instant = value as InstantExchange
      return {
        instantExchangeEnabled: instant.enabled,
        feeType: instant.feeType,
        timing: instant.timing
      }
    }
  }
}

function useExchangesMutation<Input>(
  mutationFn: (input: Input) => Promise<void>,
  group: string,
  messages: { success: string; error: string },
  tracking: {
    event: ExchangesEvent
    properties: (input: Input) => Record<string, unknown>
  }
) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const capture = (input: Input, succeeded: boolean) =>
    captureExchangesEvent(tracking.event, {
      group,
      ...tracking.properties(input),
      succeeded
    })
  const mutation = useMutation({
    mutationFn,
    onSuccess: (_, input) => {
      capture(input, true)
      toast({ text: t(messages.success), variant: 'success' })
      queryClient.invalidateQueries({ queryKey: exchangesKey(group) })
    },
    onError: (error, input) => {
      capture(input, false)
      console.warn(error)
      toast({ text: rejection(error) ?? t(messages.error), variant: 'error' })
    }
  })
  return { run: mutation.mutate, running: mutation.isPending }
}

export function useSaveGroupExchanges(group: string) {
  const { run, running } = useExchangesMutation(
    ({ kind, value }: { kind: ExchangesKind; value: ExchangesSettings }) =>
      ExchangesApi.saveGroupExchanges(group, kind, value),
    group,
    { success: 'exchanges.save_success', error: 'exchanges.save_error' },
    {
      event: 'Exchanges settings saved',
      properties: ({ kind, value }) => ({
        level: 'group',
        kind,
        ...settingsSummary(kind, value)
      })
    }
  )
  return { saveGroupExchanges: run, savingGroupExchanges: running }
}

export function useSaveStoreExchanges(group: string) {
  const { run, running } = useExchangesMutation(
    ({
      ecommerceId,
      kind,
      value
    }: {
      ecommerceId: string
      kind: ExchangesKind
      value: ExchangesSettings
    }) => ExchangesApi.saveStoreExchanges(ecommerceId, kind, value),
    group,
    { success: 'exchanges.save_success', error: 'exchanges.save_error' },
    {
      event: 'Exchanges settings saved',
      properties: ({ ecommerceId, kind, value }) => ({
        level: 'store',
        ecommerceId,
        kind,
        ...settingsSummary(kind, value)
      })
    }
  )
  return { saveStoreExchanges: run, savingStoreExchanges: running }
}

export function useResetStoreExchanges(group: string) {
  const { run, running } = useExchangesMutation(
    ({ ecommerceId, kind }: { ecommerceId: string; kind: ExchangesKind }) =>
      ExchangesApi.resetStoreExchanges(ecommerceId, kind),
    group,
    { success: 'exchanges.reset_success', error: 'exchanges.reset_error' },
    {
      event: 'Exchanges store settings reset',
      properties: ({ ecommerceId, kind }) => ({ ecommerceId, kind })
    }
  )
  return { resetStoreExchanges: run, resettingStoreExchanges: running }
}

const extraDetailsKey = (ecommerceId?: string) => [
  'extraDetailsTitles',
  ecommerceId
]

// The Full Catalog's extra details title, by language, as a portal shows it.
export function useExtraDetailsTitles(ecommerceId?: string, enabled = true) {
  const query = useQuery({
    queryKey: extraDetailsKey(ecommerceId),
    queryFn: ({ signal }) =>
      ExchangesApi.findExtraDetailsTitles(ecommerceId ?? '', signal),
    enabled: enabled && !!ecommerceId
  })
  return { titles: query.data, loadingTitles: query.isLoading }
}

export function useSaveExtraDetailsTitles() {
  const queryClient = useQueryClient()
  return async (ecommerceIds: string[], titles: Record<string, string>) => {
    await ExchangesApi.saveExtraDetailsTitles(ecommerceIds, titles)
    await Promise.all(
      ecommerceIds.map((ecommerceId) =>
        queryClient.invalidateQueries({
          queryKey: extraDetailsKey(ecommerceId)
        })
      )
    )
  }
}
